// Stack — Basic vs Optimized code for every "Beginner Problems" drill listed in the Stack
// Explanation modal. Same shape as slidingWindowDrills.js: concise steps + an interactive
// "watch it happen" visualizer per version. One Advanced drill cross-references the curated
// problem instead of duplicating content, mirroring arraysDrills.js's Kadane's stub.

const stackDrills = [
  {
    id: 'valid-parentheses',
    title: 'Check if a String of Brackets Is Valid',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Repeatedly Remove Adjacent Matching Pairs',
        explanation: 'Keep scanning the string for an adjacent matching pair — "()", "[]", or "{}" — and remove it, repeating until no more pairs can be found. If the string empties out completely, every bracket was matched. Correct, but every removal pass rescans a large chunk of the string.',
        steps: [
          'Scan the string for any adjacent "()", "[]", or "{}" and remove it.',
          'Repeat the scan on the shortened string.',
          'Stop when a full scan finds nothing left to remove.',
          'The string is valid only if it\'s now completely empty.',
        ],
        visual: {
          arrays: [{ label: 's', values: ['{', '[', '(', ')', ']', '}'] }],
          varNames: [],
          steps: [
            { highlights: { s: [2, 3] } , note: 'Pass 1 finds "()" at positions 2-3 and removes it: "{[()]}\" → \"{[]}".' },
            { highlights: { s: [1, 2] }, note: 'Pass 2 finds "[]" and removes it: "{[]}" → "{}".' },
            { highlights: { s: [0, 1] }, note: 'Pass 3 finds "{}" and removes it: "{}" → "" (empty). Nothing left — valid!' },
          ],
        },
        code:
`public boolean isValidBasic(String s) {
    String prev;
    do {
        prev = s;
        s = s.replace("()", "").replace("[]", "").replace("{}", "");
    } while (!s.equals(prev));
    return s.isEmpty();
}`,
        complexity: { time: 'O(n²)', space: 'O(n) — each replace() builds a new string' },
      },
      {
        name: 'Optimized — Stack-Based Single Pass',
        explanation: 'Push every opening bracket onto a stack. Every closing bracket must match whatever is currently on TOP of the stack — that\'s exactly what a stack gives you for free, since the most recently opened bracket must be the next one closed.',
        steps: [
          'Walk the string once, left to right.',
          'Push any opening bracket onto the stack.',
          'For a closing bracket, check the top of the stack: if it\'s the matching opener, pop it; otherwise the string is invalid immediately.',
          'After the walk, the string is valid only if the stack ended empty.',
        ],
        visual: {
          arrays: [{ label: 's', values: ['{', '[', '(', ')', ']', '}'] }, { label: 'stack', values: [] }],
          varNames: [],
          steps: [
            { highlights: { s: [0] }, snapshots: { stack: ['{'] }, note: "'{' is an opener — push it." },
            { highlights: { s: [1] }, snapshots: { stack: ['{', '['] }, note: "'[' is an opener — push it." },
            { highlights: { s: [2] }, snapshots: { stack: ['{', '[', '('] }, note: "'(' is an opener — push it." },
            { highlights: { s: [3] }, snapshots: { stack: ['{', '['] }, note: "')' matches the top '(' — pop it." },
            { highlights: { s: [4] }, snapshots: { stack: ['{'] }, note: "']' matches the top '[' — pop it." },
            { highlights: { s: [5] }, snapshots: { stack: [] }, note: "'}' matches the top '{' — pop it. Stack is empty at the end — valid, found in one pass." },
          ],
        },
        code:
`public boolean isValid(String s) {
    Deque<Character> stack = new ArrayDeque<>();
    Map<Character, Character> pairs = Map.of(')', '(', ']', '[', '}', '{');

    for (char c : s.toCharArray()) {
        if (c == '(' || c == '[' || c == '{') {
            stack.push(c);
        } else {
            if (stack.isEmpty() || stack.pop() != pairs.get(c)) {
                return false;
            }
        }
    }
    return stack.isEmpty();
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
    ],
  },
  {
    id: 'reverse-string-using-stack',
    title: 'Reverse a String Using a Stack',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — StringBuilder.reverse()',
        explanation: 'Java\'s StringBuilder already has a built-in reverse() method — the fastest way to actually solve this in production code. But it hides the mechanism, which is the whole point of this drill.',
        steps: [
          'Wrap the string in a StringBuilder.',
          'Call .reverse().',
          'Convert back to a String.',
          'Correct and fast, but doesn\'t demonstrate WHY a stack naturally reverses order — which is what generalizes to problems where no built-in reverse exists.',
        ],
        visual: {
          arrays: [{ label: 's', values: ['h', 'e', 'l', 'l', 'o'] }],
          varNames: [],
          steps: [
            { snapshots: { s: ['o', 'l', 'l', 'e', 'h'] }, note: '"hello".reverse() → "olleh", done internally in one call.' },
          ],
        },
        code:
`public String reverseBasic(String s) {
    return new StringBuilder(s).reverse().toString();
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
      {
        name: 'Optimized — Push Every Character, Then Pop Them All Off',
        explanation: 'Push every character onto a stack in order, then pop them all off. Since a stack always returns its MOST RECENTLY pushed item first, popping everything off necessarily produces the characters in reverse order — the reversal isn\'t a special algorithm, it\'s just what LIFO does automatically.',
        steps: [
          'Push every character of the string onto a stack, left to right.',
          'Pop characters off one at a time — the last one pushed comes off first.',
          'Append each popped character to a result string.',
          'The result is the string in reverse order, purely as a consequence of LIFO.',
        ],
        visual: {
          arrays: [{ label: 's', values: ['h', 'e', 'l', 'l', 'o'] }, { label: 'stack', values: [] }],
          varNames: [],
          steps: [
            { highlights: { s: [0, 1, 2, 3, 4] }, snapshots: { stack: ['h', 'e', 'l', 'l', 'o'] }, note: 'Push every character: stack (bottom to top) = h, e, l, l, o.' },
            { snapshots: { stack: ['h', 'e', 'l'] }, note: "Pop twice: 'o' then 'l' come off first — they were pushed last. result so far: \"ol\"." },
            { snapshots: { stack: [] }, note: 'Pop the rest: \'l\', \'e\', \'h\'. Final result: "olleh" — the reversal fell out of the data structure itself.' },
          ],
        },
        code:
`public String reverse(String s) {
    Deque<Character> stack = new ArrayDeque<>();
    for (char c : s.toCharArray()) stack.push(c);

    StringBuilder result = new StringBuilder();
    while (!stack.isEmpty()) {
        result.append(stack.pop());
    }
    return result.toString();
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
    ],
  },
  {
    id: 'implement-stack-using-array',
    title: 'Implement a Stack From Scratch',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Fixed-Capacity Array',
        explanation: 'Back the stack with a plain array of a fixed size, tracking the index of the top element manually. Simple and fast, but the capacity is baked in at creation time — push past that limit and there\'s nowhere for the new value to go.',
        steps: [
          'Allocate an array of a chosen fixed size, and a top index starting at -1.',
          'push: increment top, then write the value at that index — but only if there\'s room left.',
          'pop: read the value at top, then decrement top.',
          'If push is called when the array is already full, it has no way to grow — it must fail or overwrite.',
        ],
        visual: {
          arrays: [{ label: 'data (capacity 4)', values: [null, null, null, null] }],
          varNames: ['top'],
          steps: [
            { highlights: { data: [0] }, vars: { top: 0 }, snapshots: { data: [3, null, null, null] }, note: 'push(3): top becomes 0, data[0] = 3.' },
            { highlights: { data: [1] }, vars: { top: 1 }, snapshots: { data: [3, 7, null, null] }, note: 'push(7): top becomes 1, data[1] = 7.' },
            { highlights: { data: [3] }, vars: { top: 3 }, snapshots: { data: [3, 7, 9, 1] }, note: 'Two more pushes fill the array completely: top = 3, capacity reached.' },
            { note: 'A fifth push has nowhere to go — this fixed-capacity design simply cannot grow further.' },
          ],
        },
        code:
`public class FixedStack {
    private int[] data;
    private int top = -1;

    public FixedStack(int capacity) {
        data = new int[capacity];
    }

    public void push(int value) {
        if (top == data.length - 1) throw new IllegalStateException("Stack is full");
        data[++top] = value;
    }

    public int pop() {
        if (top == -1) throw new IllegalStateException("Stack is empty");
        return data[top--];
    }
}`,
        complexity: { time: 'O(1) per push/pop', space: 'O(capacity) — fixed, cannot grow' },
      },
      {
        name: 'Optimized — Dynamic Array That Doubles When Full',
        explanation: 'The same array-backed idea, but the moment the array fills up, allocate a new array at DOUBLE the size and copy everything over before continuing. A single resize costs O(n), but it happens so rarely (only when doubling) that the average cost per push, amortized over many pushes, is still O(1) — this is exactly how Java\'s own ArrayList and ArrayDeque behave internally.',
        steps: [
          'Start with a small array and a top index at -1.',
          'On push, if the array is full, allocate a new array of double the capacity and copy every element over.',
          'Write the new value at the next slot either way.',
          'Because each element is only ever copied O(log n) times across all the doublings it survives, the amortized cost per push stays O(1).',
        ],
        visual: {
          arrays: [{ label: 'data (capacity 2 → grows)', values: [null, null] }],
          varNames: ['top', 'capacity'],
          steps: [
            { vars: { top: 1, capacity: 2 }, snapshots: { data: [3, 7] }, note: 'Two pushes fill the initial capacity-2 array: [3, 7].' },
            { vars: { top: 2, capacity: 4 }, snapshots: { data: [3, 7, 9, null] }, note: 'A third push finds the array full — double capacity to 4, copy [3,7] over, then write 9. Costs O(n) this one time.' },
            { vars: { top: 3, capacity: 4 }, snapshots: { data: [3, 7, 9, 1] }, note: 'A fourth push fits in the already-doubled array — back to O(1), no copying needed.' },
          ],
        },
        code:
`public class DynamicStack {
    private int[] data = new int[2];
    private int top = -1;

    public void push(int value) {
        if (top == data.length - 1) {
            data = Arrays.copyOf(data, data.length * 2);   // O(n), but rare
        }
        data[++top] = value;
    }

    public int pop() {
        if (top == -1) throw new IllegalStateException("Stack is empty");
        return data[top--];
    }
}`,
        complexity: { time: 'O(1) amortized per push/pop', space: 'O(n) — grows as needed, never overflows' },
      },
    ],
  },
  {
    id: 'next-greater-element',
    title: 'Next Greater Element for Every Array Position',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Scan Right From Every Position',
        explanation: 'For every element, scan forward until a bigger one is found. Direct, but every element triggers its own scan even though a lot of that scanning repeats work already done for earlier elements.',
        steps: [
          'For each index i, scan every later index j looking for the first value bigger than arr[i].',
          'Record that value, or -1 if none is found before the array ends.',
          'Every position gets its own forward scan — O(n²) in the worst case.',
        ],
        code:
`public int[] nextGreaterBasic(int[] arr) {
    int[] result = new int[arr.length];
    for (int i = 0; i < arr.length; i++) {
        result[i] = -1;
        for (int j = i + 1; j < arr.length; j++) {
            if (arr[j] > arr[i]) {
                result[i] = arr[j];
                break;
            }
        }
    }
    return result;
}`,
        complexity: { time: 'O(n²)', space: 'O(n) — for the result array' },
      },
      {
        name: 'Optimized — Monotonic Decreasing Stack',
        explanation: 'Keep a stack of indices whose "next greater" hasn\'t been found yet, always in decreasing order of value from bottom to top. When a new element is bigger than the value on top of the stack, that\'s the answer for every index the new element beats — pop them all, resolving several answers in one visit.',
        steps: [
          'Walk the array once, keeping a stack of unresolved indices.',
          'While the current value is bigger than arr[stack top], pop that index and record the current value as its answer.',
          'Push the current index — its own answer isn\'t known yet.',
          'Any index still on the stack when the array ends never found a bigger value — its answer stays -1.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [4, 5, 2, 10, 8] }, { label: 'stack (values shown)', values: [] }],
          varNames: [],
          steps: [
            { highlights: { arr: [0] }, snapshots: { 'stack (values shown)': [4] }, note: 'i=0 (4): stack empty — push. stack = [4].' },
            { highlights: { arr: [1] }, snapshots: { 'stack (values shown)': [5] }, note: 'i=1 (5): 5 > top of stack (4) — pop it, its answer is 5. Push 5. stack = [5].' },
            { highlights: { arr: [2] }, snapshots: { 'stack (values shown)': [5, 2] }, note: 'i=2 (2): 2 is not bigger than the top (5) — just push. stack = [5, 2].' },
            { highlights: { arr: [3] }, snapshots: { 'stack (values shown)': [10] }, note: 'i=3 (10): bigger than top (2) — pop, answer 10. Still bigger than new top (5) — pop, answer 10 too. Push 10. stack = [10].' },
            { highlights: { arr: [4] }, snapshots: { 'stack (values shown)': [10, 8] }, note: 'i=4 (8): not bigger than top (10) — just push. stack = [10, 8]. Array ends with both still on the stack — their answers stay -1. Final: [5, 10, 10, -1, -1].' },
          ],
        },
        code:
`public int[] nextGreater(int[] arr) {
    int[] result = new int[arr.length];
    Arrays.fill(result, -1);
    Deque<Integer> stack = new ArrayDeque<>();  // indices, values decreasing

    for (int i = 0; i < arr.length; i++) {
        while (!stack.isEmpty() && arr[stack.peek()] < arr[i]) {
            result[stack.pop()] = arr[i];
        }
        stack.push(i);
    }
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
    ],
  },
  {
    id: 'min-stack-design',
    title: 'Design a Stack That Tracks Its Own Minimum',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Scan the Whole Stack on Every getMin() Call',
        explanation: 'push and pop work exactly like a normal stack. For getMin(), just scan every element currently on the stack and return the smallest. Correct, but every single getMin() call redoes the entire scan, even if nothing has changed since the last call.',
        steps: [
          'push and pop behave like an ordinary stack — no extra bookkeeping.',
          'For getMin(), scan every element on the stack.',
          'Return the smallest value found.',
          'Every call to getMin() costs O(n), no matter how many times it\'s called in a row.',
        ],
        code:
`public class MinStackBasic {
    private Deque<Integer> stack = new ArrayDeque<>();

    public void push(int v) { stack.push(v); }
    public void pop() { stack.pop(); }

    public int getMin() {
        int min = Integer.MAX_VALUE;
        for (int v : stack) min = Math.min(min, v);
        return min;
    }
}`,
        complexity: { time: 'O(1) push/pop, O(n) getMin()', space: 'O(n)' },
      },
      {
        name: 'Optimized — A Parallel minStack Tracks the Running Minimum',
        explanation: 'Keep a second stack, minStack, that mirrors the main stack one-for-one. Every push also pushes the CURRENT minimum (either minStack\'s existing top, or the new value if it\'s smaller) onto minStack. getMin() then just reads minStack\'s top — always O(1), because the running minimum was maintained incrementally instead of recomputed.',
        steps: [
          'Keep two stacks: the main one, and minStack.',
          'On push(v): push v onto the main stack, and push min(minStack\'s current top, v) onto minStack (or just v if minStack is empty).',
          'On pop(): pop from both stacks together, keeping them in sync.',
          'getMin() is just minStack.peek() — O(1), every time.',
        ],
        visual: {
          arrays: [{ label: 'stack', values: [] }, { label: 'minStack', values: [] }],
          varNames: [],
          steps: [
            { snapshots: { stack: [5], minStack: [5] }, note: 'push(5): minStack is empty, so 5 is also the minimum. minStack = [5].' },
            { snapshots: { stack: [5, 2], minStack: [5, 2] }, note: 'push(2): 2 < minStack top (5) — the new minimum is 2. minStack = [5, 2].' },
            { snapshots: { stack: [5, 2, 4], minStack: [5, 2, 2] }, note: 'push(4): 4 is not smaller than minStack top (2) — minimum stays 2. minStack = [5, 2, 2]. getMin() reads the top instantly: 2.' },
            { snapshots: { stack: [5, 2], minStack: [5, 2] }, note: 'pop(): both stacks lose their top together. getMin() is now 2 again — correctly restored, with no rescanning.' },
          ],
        },
        code:
`public class MinStack {
    private Deque<Integer> stack = new ArrayDeque<>();
    private Deque<Integer> minStack = new ArrayDeque<>();

    public void push(int v) {
        stack.push(v);
        int newMin = minStack.isEmpty() ? v : Math.min(minStack.peek(), v);
        minStack.push(newMin);
    }

    public void pop() {
        stack.pop();
        minStack.pop();
    }

    public int getMin() {
        return minStack.peek();
    }
}`,
        complexity: { time: 'O(1) for push, pop, and getMin()', space: 'O(n) — the extra minStack' },
      },
    ],
  },
  {
    id: 'evaluate-postfix',
    title: 'Evaluate a Postfix (Reverse Polish) Expression',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Recursively Find and Resolve the Last Operator',
        explanation: 'A postfix expression\'s outermost operator is always its LAST token. Recursively split the expression around that operator, evaluate each side (which is itself a smaller postfix expression), and combine. Correct, but finding the last operator\'s operands requires rescanning shrinking substrings at every level of recursion.',
        steps: [
          'Find the last token — it\'s always the final operator to apply.',
          'The tokens before it form two smaller postfix sub-expressions for the two operands.',
          'Recursively evaluate each sub-expression the same way.',
          'Combine the two results with the operator. Splitting and rescanning at every level adds up to O(n²) in the worst case.',
        ],
        code:
`// Simplified sketch — real splitting logic must track operand boundaries carefully.
public int evalPostfixBasic(String[] tokens) {
    if (tokens.length == 1) return Integer.parseInt(tokens[0]);
    String op = tokens[tokens.length - 1];
    String[] rest = Arrays.copyOf(tokens, tokens.length - 1);
    int splitPoint = findOperandSplit(rest);  // rescans 'rest' to find where operand 2 begins
    int left = evalPostfixBasic(Arrays.copyOfRange(rest, 0, splitPoint));
    int right = evalPostfixBasic(Arrays.copyOfRange(rest, splitPoint, rest.length));
    return applyOp(left, right, op);
}`,
        complexity: { time: 'O(n²)', space: 'O(n) — recursion depth plus substring copies' },
      },
      {
        name: 'Optimized — Iterative Single-Pass Stack Evaluation',
        explanation: 'Walk the tokens once. Push every number. When an operator appears, its two operands are exactly the top two values on the stack — pop them, apply the operator, and push the result back. No splitting, no recursion, no rescanning.',
        steps: [
          'Walk the tokens left to right.',
          'If a token is a number, push it.',
          'If a token is an operator, pop the top two values, apply the operator, and push the result.',
          'After the last token, exactly one value remains on the stack — the final answer.',
        ],
        visual: {
          arrays: [{ label: 'tokens', values: ['2', '3', '4', '*', '+'] }, { label: 'stack', values: [] }],
          varNames: [],
          steps: [
            { highlights: { tokens: [0] }, snapshots: { stack: [2] }, note: "'2' is a number — push it." },
            { highlights: { tokens: [1] }, snapshots: { stack: [2, 3] }, note: "'3' is a number — push it." },
            { highlights: { tokens: [2] }, snapshots: { stack: [2, 3, 4] }, note: "'4' is a number — push it." },
            { highlights: { tokens: [3] }, snapshots: { stack: [2, 12] }, note: "'*' pops 4 and 3, computes 3 × 4 = 12, pushes it back." },
            { highlights: { tokens: [4] }, snapshots: { stack: [14] }, note: "'+' pops 12 and 2, computes 2 + 12 = 14, pushes it back. One value remains — the answer: 14." },
          ],
        },
        code:
`public int evalPostfix(String[] tokens) {
    Deque<Integer> stack = new ArrayDeque<>();
    for (String token : tokens) {
        switch (token) {
            case "+": stack.push(stack.pop() + stack.pop()); break;
            case "*": stack.push(stack.pop() * stack.pop()); break;
            case "-": { int b = stack.pop(), a = stack.pop(); stack.push(a - b); break; }
            case "/": { int b = stack.pop(), a = stack.pop(); stack.push(a / b); break; }
            default: stack.push(Integer.parseInt(token));
        }
    }
    return stack.pop();
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
    ],
  },
  {
    id: 'daily-temperatures',
    title: 'Daily Temperatures (Days Until a Warmer Day)',
    level: 'Advanced',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Daily Temperatures\" there for the complete monotonic-stack step-by-step and both code versions. It's " +
      "the same core idea as the Next Greater Element drill above, just answering \"how many days until\" " +
      "instead of \"what is the next greater value.\"",
  },
  {
    id: 'largest-rectangle-histogram',
    title: 'Largest Rectangle in a Histogram',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Expand Outward From Every Bar',
        explanation: 'For every bar, treat it as the SHORTEST bar in some rectangle, and expand left and right as far as possible while every bar in that range is at least as tall. That gives the widest rectangle with that bar as the limiting height. Correct, but every bar triggers its own left/right expansion.',
        steps: [
          'For each bar, expand left while neighboring bars are ≥ its height.',
          'Expand right the same way.',
          'The rectangle\'s area is (right boundary − left boundary) × this bar\'s height.',
          'Track the maximum area across every bar — each expansion is O(n) in the worst case, O(n²) total.',
        ],
        code:
`public int largestRectangleBasic(int[] heights) {
    int maxArea = 0;
    for (int i = 0; i < heights.length; i++) {
        int left = i;
        while (left > 0 && heights[left - 1] >= heights[i]) left--;
        int right = i;
        while (right < heights.length - 1 && heights[right + 1] >= heights[i]) right++;
        int width = right - left + 1;
        maxArea = Math.max(maxArea, width * heights[i]);
    }
    return maxArea;
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Monotonic Increasing Stack',
        explanation: 'Keep a stack of bar indices with strictly increasing heights, bottom to top. The moment a shorter bar appears, every taller bar still on the stack has just found its RIGHT boundary (this shorter bar), and its LEFT boundary is whatever remains below it on the stack — so its final area can be computed and it can be popped, once, forever.',
        steps: [
          'Walk the bars left to right (with one extra 0-height sentinel bar appended at the end to flush the stack).',
          'While the current bar is shorter than the bar on top of the stack, pop it and compute its rectangle: height = the popped bar\'s height, width = current index − (new stack top\'s index) − 1 (or the current index, if the stack is now empty).',
          'Track the maximum area found from each popped bar.',
          'Push the current index. Each bar is pushed once and popped once — O(n) total.',
        ],
        visual: {
          arrays: [{ label: 'heights (+ sentinel 0)', values: [2, 1, 5, 6, 2, 3, 0] }],
          varNames: ['maxArea'],
          steps: [
            { highlights: { heights: [1] }, vars: { maxArea: 2 }, note: 'i=1 (height 1) is shorter than the stacked bar at i=0 (height 2) — pop it. Width = i = 1 (stack now empty). Area = 2×1 = 2.' },
            { highlights: { heights: [4] }, vars: { maxArea: 10 }, note: 'i=4 (height 2) is shorter than the stacked bars. Pop index 3 (height 6): width = i − (new top=2) − 1 = 1, area = 6. Pop index 2 (height 5): width = i − (new top=1) − 1 = 2, area = 10 — new max!' },
            { highlights: { heights: [6] }, vars: { maxArea: 10 }, note: 'i=6, the sentinel (height 0), pops everything remaining. Popping index 5 (height 3): width=1, area=3. Popping index 4 (height 2): width=4, area=8. Popping index 1 (height 1): width=6, area=6. None beat 10. Final answer: 10.' },
          ],
        },
        code:
`public int largestRectangle(int[] heights) {
    Deque<Integer> stack = new ArrayDeque<>(); // indices, increasing heights
    int maxArea = 0;

    for (int i = 0; i <= heights.length; i++) {
        int h = (i == heights.length) ? 0 : heights[i];  // sentinel flushes the stack
        while (!stack.isEmpty() && heights[stack.peek()] > h) {
            int height = heights[stack.pop()];
            int width = stack.isEmpty() ? i : i - stack.peek() - 1;
            maxArea = Math.max(maxArea, height * width);
        }
        stack.push(i);
    }
    return maxArea;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
    ],
  },
  {
    id: 'decode-string',
    title: 'Decode a Nested Encoded String',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Recursive Descent',
        explanation: 'Whenever a digit starts a new k[...] group, recursively decode whatever is inside the brackets, then repeat that decoded piece k times. Correct and readable, but recursion depth grows with nesting depth — a deeply nested input risks a stack overflow from Java\'s own call stack.',
        steps: [
          'Read a number (the repeat count) followed by \'[\'.',
          'Recursively decode everything up to the matching \']\'.',
          'Repeat that decoded substring the given number of times.',
          'Concatenate with whatever comes before and after — but each recursive call adds a frame to Java\'s call stack.',
        ],
        code:
`// Simplified sketch — a full version needs an index tracked across recursive calls.
public String decodeBasic(String s, int[] pos) {
    StringBuilder result = new StringBuilder();
    while (pos[0] < s.length() && s.charAt(pos[0]) != ']') {
        if (Character.isDigit(s.charAt(pos[0]))) {
            int count = 0;
            while (Character.isDigit(s.charAt(pos[0]))) count = count * 10 + (s.charAt(pos[0]++) - '0');
            pos[0]++; // skip '['
            String inner = decodeBasic(s, pos);
            pos[0]++; // skip ']'
            result.append(inner.repeat(count));
        } else {
            result.append(s.charAt(pos[0]++));
        }
    }
    return result.toString();
}`,
        complexity: { time: 'O(n · maxRepeat)', space: 'O(depth) — Java\'s own call stack, one frame per nesting level' },
      },
      {
        name: 'Optimized — Iterative With Two Explicit Stacks',
        explanation: 'Replace the recursion with two explicit stacks: one holding pending repeat counts, one holding the string built so far at each nesting level. On \']\', pop both and fold the completed inner string into what was being built one level out — the exact same logic as recursion, just made explicit instead of relying on Java\'s call stack.',
        steps: [
          'Walk the string once. On a digit, accumulate the repeat count.',
          'On \'[\', push the current count and the string built so far, then reset both to start the new nested level.',
          'On a regular character, append it to the current string being built.',
          'On \']\', pop the saved count and outer string, and append the current (completed) string repeated that many times onto the outer string.',
        ],
        visual: {
          arrays: [{ label: 's', values: ['3', '[', 'a', '2', '[', 'c', ']', ']'] }, { label: 'countStack', values: [] }, { label: 'stringStack', values: [] }],
          varNames: ['current'],
          steps: [
            { highlights: { s: [0] }, vars: { current: '' }, note: "'3' — start accumulating a count: 3." },
            { highlights: { s: [1] }, snapshots: { countStack: [3], stringStack: [''] }, vars: { current: '' }, note: "'[' — push count 3 and the current (empty) string. Reset current to build the new nested level." },
            { highlights: { s: [2] }, vars: { current: 'a' }, note: "'a' — append to current: \"a\"." },
            { highlights: { s: [3] }, vars: { current: 'a' }, note: "'2' — start accumulating a new count: 2." },
            { highlights: { s: [4] }, snapshots: { countStack: [3, 2], stringStack: ['', 'a'] }, vars: { current: '' }, note: "'[' — push count 2 and current (\"a\"). Reset current." },
            { highlights: { s: [5] }, vars: { current: 'c' }, note: "'c' — append: \"c\"." },
            { highlights: { s: [6] }, snapshots: { countStack: [3], stringStack: [''] }, vars: { current: 'acc' }, note: "']' — pop count 2 and outer string \"a\". New current = \"a\" + \"c\".repeat(2) = \"acc\"." },
            { highlights: { s: [7] }, snapshots: { countStack: [], stringStack: [] }, vars: { current: 'accaccacc' }, note: "']' — pop count 3 and outer string \"\". New current = \"\" + \"acc\".repeat(3) = \"accaccacc\". Final answer." },
          ],
        },
        code:
`public String decodeString(String s) {
    Deque<Integer> countStack = new ArrayDeque<>();
    Deque<StringBuilder> stringStack = new ArrayDeque<>();
    StringBuilder current = new StringBuilder();
    int count = 0;

    for (char c : s.toCharArray()) {
        if (Character.isDigit(c)) {
            count = count * 10 + (c - '0');
        } else if (c == '[') {
            countStack.push(count);
            stringStack.push(current);
            current = new StringBuilder();
            count = 0;
        } else if (c == ']') {
            StringBuilder outer = stringStack.pop();
            int repeat = countStack.pop();
            for (int i = 0; i < repeat; i++) outer.append(current);
            current = outer;
        } else {
            current.append(c);
        }
    }
    return current.toString();
}`,
        complexity: { time: 'O(n · maxRepeat)', space: 'O(depth) — two explicit stacks instead of Java\'s call stack' },
      },
    ],
  },
];

export default stackDrills;
